"use client";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import * as React from "react";
import { AncestorLayerContext } from "../../lib/backdrop-layer";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import { useVisualViewportBottomInset } from "../../hooks/use-visual-viewport-inset";
import { useTouchAwareInitialFocus } from "../../hooks/use-open-focus";
import {
  BOX_SURFACE_SHADOW,
  SURFACE_BG,
  type SurfaceToken,
} from "./box-classes";
import { Button } from "./button";
import { Icon } from "./icon";
import { ScrollArea } from "./scroll-area";
import type { Omittable } from "../../lib/props";

export const Sheet: typeof SheetPrimitive.Root = SheetPrimitive.Root;

export const SheetPortal: typeof SheetPrimitive.Portal = SheetPrimitive.Portal;

export function SheetTrigger(
  props: SheetPrimitive.Trigger.Props,
): React.ReactElement {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

export function SheetClose(
  props: SheetPrimitive.Close.Props,
): React.ReactElement {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

export function SheetBackdrop({
  className,
  ...props
}: SheetPrimitive.Backdrop.Props): React.ReactElement {
  // Base UI drops a nested layer's backdrop on the assumption an ancestor
  // dimmed the page. Every modal layer paints its own scrim instead, so a
  // sheet dims whatever layer it opens over. See AncestorLayerContext.
  return (
    <SheetPrimitive.Backdrop
      forceRender
      className={cn(
        "fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-all duration-base data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="sheet-backdrop"
      {...props}
    />
  );
}

export const sheetViewportVariants = cva("fixed inset-0 z-50 grid", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    side: {
      bottom: "grid grid-rows-[1fr_auto] pt-12",
      top: "grid grid-rows-[auto_1fr] pb-12",
      left: "flex justify-start",
      right: "flex justify-end",
    },
    variant: {
      default: "",
      inset: "sm:p-4",
      bare: "",
    },
    clickBehind: {
      true: "pointer-events-none",
    },
  },
});

export function SheetViewport({
  className,
  side,
  variant,
  clickBehind,
  ...props
}: SheetPrimitive.Viewport.Props & Omittable<{
  side?: VariantProps<typeof sheetViewportVariants>["side"];
  variant?: VariantProps<typeof sheetViewportVariants>["variant"];
  /** Lets pointer events pass through the viewport to the page beneath —
   *  the viewport is a `fixed inset-0` element, so without this every click
   *  outside the popup dies here even when the sheet is non-modal. */
  clickBehind?: VariantProps<typeof sheetViewportVariants>["clickBehind"];
}>): React.ReactElement {
  // Keyboard compensation: the layout viewport this `fixed inset-0` element
  // sizes against does not shrink for the software keyboard on iOS Safari
  // (or any `resizes-visual` browser); the measured overlap becomes bottom
  // padding so bottom-anchored content stays above the keyboard.
  const keyboardInset = useVisualViewportBottomInset();
  return (
    <SheetPrimitive.Viewport
      className={cn(
        sheetViewportVariants({ clickBehind, side, variant }),
        className,
      )}
      data-slot="sheet-viewport"
      style={keyboardInset > 0 ? { paddingBottom: keyboardInset } : undefined}
      {...props}
    />
  );
}

export const sheetPopupVariants = cva(
  "pointer-events-auto relative flex max-h-full min-h-0 w-full min-w-0 flex-col not-dark:bg-clip-padding outline-none transition-[opacity,translate] duration-slow ease-spring will-change-transform before:pointer-events-none before:absolute before:inset-0 before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 max-sm:before:hidden dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
  {
    compoundVariants: [
      // A quiet-surface panel shares the page fill, so the seam hairline is
      // its only edge — and content scrolling under it reads as the same
      // plane. A whisper of directional depth off the OPEN edge (the token
      // surface shadows are ambient and invisible on a full-height edge)
      // keeps the panel legible as "over" without lifting it to a popover.
      // Physical offsets on purpose: box-shadow has no logical direction.
      {
        class:
          "shadow-[-10px_0_15px_-10px_--theme(--color-black/6%)] dark:shadow-[-10px_0_15px_-10px_--theme(--color-black/32%)]",
        side: "right",
        surface: ["sunken", "background", "sidebar"],
        variant: "default",
      },
      {
        class:
          "shadow-[10px_0_15px_-10px_--theme(--color-black/6%)] dark:shadow-[10px_0_15px_-10px_--theme(--color-black/32%)]",
        side: "left",
        surface: ["sunken", "background", "sidebar"],
        variant: "default",
      },
      {
        class:
          "shadow-[0_-10px_15px_-10px_--theme(--color-black/6%)] dark:shadow-[0_-10px_15px_-10px_--theme(--color-black/32%)]",
        side: "bottom",
        surface: ["sunken", "background", "sidebar"],
        variant: "default",
      },
      {
        class:
          "shadow-[0_10px_15px_-10px_--theme(--color-black/6%)] dark:shadow-[0_10px_15px_-10px_--theme(--color-black/32%)]",
        side: "top",
        surface: ["sunken", "background", "sidebar"],
        variant: "default",
      },
    ],
    defaultVariants: {
      // `elevated`, not `popover`: a sheet is a room, and dark popovers lift
      // past the card rung for transient popups — see `dialogPopupVariants`.
      surface: "elevated",
      variant: "default",
    },
    variants: {
      // Before the side/variant lines so `bare` still out-merges the
      // surface's fill and shadow away.
      surface: {
        sunken: `${SURFACE_BG.sunken} ${BOX_SURFACE_SHADOW.sunken}`,
        background: `${SURFACE_BG.background} ${BOX_SURFACE_SHADOW.background}`,
        card: `${SURFACE_BG.card} ${BOX_SURFACE_SHADOW.card}`,
        elevated: `${SURFACE_BG.elevated} ${BOX_SURFACE_SHADOW.elevated}`,
        popover: `${SURFACE_BG.popover} ${BOX_SURFACE_SHADOW.popover}`,
        sidebar: `${SURFACE_BG.sidebar} ${BOX_SURFACE_SHADOW.sidebar}`,
      } satisfies Record<SurfaceToken, string>,
      side: {
        bottom:
          // Meets the screen edge → owes the home-indicator safe area.
          "row-start-2 border-t pb-[env(safe-area-inset-bottom,0px)] data-ending-style:translate-y-8 data-starting-style:translate-y-8",
        top: "border-b data-ending-style:-translate-y-8 data-starting-style:-translate-y-8",
        left: "w-[calc(100%-(--spacing(12)))] max-w-md border-e data-ending-style:-translate-x-8 data-starting-style:-translate-x-8",
        right:
          "col-start-2 w-[calc(100%-(--spacing(12)))] max-w-md border-s data-ending-style:translate-x-8 data-starting-style:translate-x-8",
      },
      variant: {
        default: "",
        inset:
          // The radius clips child fills (footer, tables) itself — no
          // descendant has to round its own bottom corners to fit.
          "before:hidden sm:overflow-clip sm:rounded-xl sm:border sm:before:rounded-[calc(var(--radius-xl)-1px)]",
        bare: "border-0 bg-transparent shadow-none before:hidden",
      },
    },
  },
);

// An anchored sheet borders on ONE edge — the inline-start hairline
// `side="right"` already gives it. Its other three edges sit flush against
// the region's inner edges, so a border there doubles the region's own, and a
// radius on those corners rounds inside a square region. The single radius it
// owes is the bottom-right, which lands ON the region's rounded inner corner
// (radius md, minus the region's 1px border): square-cut, the sheet would clip
// the corner off the region.
const ANCHORED_SHEET_CLASS = "rounded-br-[calc(var(--radius-md)-1px)]";

export function SheetPopup({
  className,
  children,
  showCloseButton = true,
  showBackdrop = true,
  side = "right",
  variant,
  surface,
  closeProps,
  portalProps,
  initialFocus,
  anchorInsets,
  style,
  ref,
  ...props
}: SheetPrimitive.Popup.Props & Omittable<{
  showCloseButton?: boolean;
  /**
   * The surface the sheet paints. The default reads as a floating panel,
   * like a dialog. Name a quieter surface for a sheet that is the mobile
   * presentation of an inline region — a nav drawer holding the same chrome
   * the desktop layout renders in a sidebar — so it paints the same surface in
   * both places instead of lifting off the page only on mobile.
   */
  surface?: SurfaceToken;
  /** A non-modal sheet (`<Sheet modal={false}>`) that must leave the page
   *  visible and clickable turns the scrim off. The backdrop is a real
   *  viewport-covering element, so `modal={false}` alone still dims and
   *  intercepts. */
  showBackdrop?: boolean;
  side?: VariantProps<typeof sheetPopupVariants>["side"];
  /**
   * `bare` strips the popup's own chrome — background, border, shadow and
   * hairline — leaving a positioned, animated container. For a sheet whose
   * children already carry their own surfaces, so the sheet does not paint a
   * slab behind them.
   */
  variant?: VariantProps<typeof sheetPopupVariants>["variant"];
  closeProps?: SheetPrimitive.Close.Props;
  portalProps?: SheetPrimitive.Portal.Props;
  /**
   * Pins the sheet inside a region of the page instead of the viewport: the
   * measured distances from the viewport's edges to that region's. The sheet
   * then borders on its open edge only — its other edges sit flush against
   * the region's — and rounds the one corner that lands on the region's own
   * rounded corner. For a panel that belongs to a view (a manage panel under
   * the view tabs), so the chrome above it stays visible and clickable.
   */
  anchorInsets?: { top?: number; right?: number; bottom?: number; left?: number };
  style?: React.CSSProperties;
}>): React.ReactElement {
  // A coarse-pointer open lands on the popup, not on the first field inside
  // it (Base UI reads the click, which WebKit stamps "mouse" for a finger);
  // a field that claimed focus by its own `autoFocus` keeps it.
  const touchFocus = useTouchAwareInitialFocus<HTMLDivElement>(ref);
  const viewport = (
    <SheetViewport clickBehind={!showBackdrop} side={side} variant={variant}>
      <SheetPrimitive.Popup
        className={cn(
          sheetPopupVariants({ side, surface, variant }),
          anchorInsets && ANCHORED_SHEET_CLASS,
          className,
        )}
        data-slot="sheet-popup"
        initialFocus={initialFocus ?? touchFocus.initialFocus}
        ref={touchFocus.ref}
        style={
          anchorInsets
            ? {
                marginTop: anchorInsets.top,
                marginRight: anchorInsets.right,
                marginBottom: anchorInsets.bottom,
                marginLeft: anchorInsets.left,
                ...style,
              }
            : style
        }
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            aria-label="Close"
            className="absolute end-2 top-2"
            render={<Button size="icon-xs" color="neutral" />}
            {...closeProps}
          >
            <Icon name="x" />
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetViewport>
  );

  return (
    <SheetPortal {...portalProps}>
      {showBackdrop && <SheetBackdrop />}
      {/* A backdrop-less sheet is a side panel over a page that stays live:
          the viewport goes click-through and the popup re-arms itself, so a
          click beside the panel lands on the page instead of dying on the
          full-viewport container. Either way the sheet is a modal-kind layer
          to what opens inside it: a drawer raised from the panel paints its
          own scrim (only a drawer-on-drawer stack withholds one). */}
      <AncestorLayerContext.Provider value="modal">{viewport}</AncestorLayerContext.Provider>
    </SheetPortal>
  );
}

export function SheetHeader({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex flex-col gap-2 p-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pb-3 max-sm:pb-4",
      className,
    ),
    "data-slot": "sheet-header",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export const sheetFooterVariants = cva(
  [
    "flex flex-col-reverse gap-2 px-6 sm:flex-row sm:justify-end",
    // Without a SheetPanel the body has no bottom gutter of its own; the
    // margin restores the gap and is inert when a panel is present.
    "in-[[data-slot=sheet-popup]:not(:has([data-slot=sheet-panel]))]:mt-4",
  ],
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default: "border-t py-4",
        bare: "in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pt-3 pt-4 pb-6",
      },
    },
  },
);

export function SheetFooter({
  className,
  variant,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  variant?: VariantProps<typeof sheetFooterVariants>["variant"];
}>): React.ReactElement {
  const defaultProps = {
    className: cn(sheetFooterVariants({ variant }), className),
    "data-slot": "sheet-footer",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function SheetTitle({
  className,
  visuallyHidden = false,
  ...props
}: SheetPrimitive.Title.Props &
  Omittable<{
    /** Names the sheet for assistive technology without painting: the
     *  visible header is something else (a breadcrumb, a search field). */
    visuallyHidden?: boolean;
  }>): React.ReactElement {
  return (
    <SheetPrimitive.Title
      className={cn(
        "font-heading font-semibold text-xl leading-none",
        visuallyHidden && "sr-only",
        className,
      )}
      data-slot="sheet-title"
      {...props}
    />
  );
}

export function SheetDescription({
  className,
  visuallyHidden = false,
  ...props
}: SheetPrimitive.Description.Props &
  Omittable<{
    /** Names the sheet for assistive technology without painting: the
     *  visible header is something else (a breadcrumb, a search field). */
    visuallyHidden?: boolean;
  }>): React.ReactElement {
  return (
    <SheetPrimitive.Description
      className={cn(
        "text-muted-foreground text-sm",
        visuallyHidden && "sr-only",
        className,
      )}
      data-slot="sheet-description"
      {...props}
    />
  );
}

export function SheetPanel({
  className,
  scrollFade = true,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  scrollFade?: boolean;
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      "p-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-header])]:pt-1 in-[[data-slot=sheet-popup]:has([data-slot=sheet-footer]:not(.border-t))]:pb-1",
      className,
    ),
    "data-slot": "sheet-panel",
  };

  return (
    <ScrollArea scrollFade={scrollFade}>
      {useRender({
        defaultTagName: "div",
        props: mergeProps<"div">(defaultProps, props),
        render,
      })}
    </ScrollArea>
  );
}
